import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  List,
  ListItem,
  Divider,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Send as SendIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import '../../styles/StudentWall.css';
import StudentWallNavbar from '../../components/StudentWallNavbar';
import axiosInstance from '../../utils/axiosInstance';
import config from '../../config.json';
import url from '../../url.json';
import io from 'socket.io-client';
import { sendAnalyticsEvent } from '../../utils/analytics';
import { CreateQuestion } from '../../components/CreateQuestion';

// 與老師端 ClassApplicationQuestionWall 分類一致
// const categories = ['全部', '資訊探詢型', '概念連結型', '延伸探究型', '反思評析型'];
// const categoryDescriptions = {
//   '全部': '顯示所有問題',
//   '資訊探詢型': '尋找尚未掌握的事實或概念，以補足知識缺口。',
//   '概念連結型': '探問概念或現象之間的關聯，用於理解與解釋。',
//   '延伸探究型': '在原有情境外提出假設或推演，以拓展探究方向。',
//   '反思評析型': '對方法、推論或證據進行檢視或評估，以提升探究品質。'
// };
const sortOptions = ['最新', '最舊', '精選優先', '按組別'];

export default function QuestionWall() {
  const { courseDispatchId, groupId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentWall, setCurrentWall] = useState('question');
  //const [categoryFilter, setCategoryFilter] = useState('全部');
  const [sortBy, setSortBy] = useState('最新');
  const [ws, setWs] = useState(null); // WebSocket 連接
  const [openQuestionDialog, setOpenQuestionDialog] = useState(false);
  const [selectedTeacherQuestion, setSelectedTeacherQuestion] = useState(null);
  const [newQuestion, setNewQuestion] = useState({
    content: '',
    task_id: null  // 關聯的關鍵提問（task_id）
  });

  // 老師關鍵提問（來自 ApplicationTask，use_brainstorm_wall = true）
  const [teacherKeyQuestions, setTeacherKeyQuestions] = useState([]);
  
  // 學生問題
  const [questions, setQuestions] = useState([]);
  
  // 追蹤學生是否已針對每個關鍵提問提出過問題
  const [submittedTaskIds, setSubmittedTaskIds] = useState(new Set());
  // Tool Use / Reading 事件（學生進入/離開問題牆）
  useEffect(() => {
    const actorId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
    if (!courseDispatchId || !actorId) return;
    
    // 發送開始事件
    sendAnalyticsEvent({ courseDispatchId, type: 'ToolUseStarted', tool: 'question-wall', actorId });
    sendAnalyticsEvent({ courseDispatchId, type: 'ReadingStarted', tool: 'question-wall', actorId, objectId: 'question-wall', objectType: 'Wall' });
    
    // 發送結束事件的函數
    const sendEndEvents = (useBeacon = false) => {
      sendAnalyticsEvent({ courseDispatchId, type: 'ToolUseEnded', tool: 'question-wall', actorId, useBeacon });
      sendAnalyticsEvent({ courseDispatchId, type: 'ReadingEnded', tool: 'question-wall', actorId, objectId: 'question-wall', objectType: 'Wall', useBeacon });
    };
    
    // 處理頁面關閉或切換（使用 keepalive 確保事件發送）
    const handleBeforeUnload = () => {
      sendEndEvents(true); // 使用 keepalive
    };
    
    // 處理頁面隱藏（切換到其他標籤或最小化）
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // 頁面隱藏時發送結束事件
        sendEndEvents(false);
      } else {
        // 頁面重新顯示時發送開始事件
        sendAnalyticsEvent({ courseDispatchId, type: 'ToolUseStarted', tool: 'question-wall', actorId });
        sendAnalyticsEvent({ courseDispatchId, type: 'ReadingStarted', tool: 'question-wall', actorId, objectId: 'question-wall', objectType: 'Wall' });
      }
    };
    
    // 處理頁面卸載（pagehide 比 beforeunload 更可靠）
    const handlePageHide = () => {
      sendEndEvents(true); // 使用 keepalive
    };
    
    // 註冊事件監聽器
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // cleanup：正常離開頁面時發送結束事件
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      sendEndEvents(false);
    };
  }, [courseDispatchId]);

  const handleWallChange = (wall) => {
    setCurrentWall(wall);
    // 優先使用 myGroupId（學生實際所在的小組），而不是 viewingGroupId（可能是在查看其他組別）
    const myGroupId = sessionStorage.getItem('myGroupId');
    const activeGroupId = myGroupId || groupId || sessionStorage.getItem('groupId') || '1';
    console.log('🔍 [QuestionWall] handleWallChange 使用的小組ID:', {
      'myGroupId (優先)': myGroupId,
      'groupId (URL)': groupId,
      'sessionStorage groupId': sessionStorage.getItem('groupId'),
      '最終使用': activeGroupId
    });
    if (wall === 'idea') {
      navigate(`/student/idea-wall/${courseDispatchId}/${activeGroupId}`);
    } else if (wall === 'cross_group') {
      // 跨組討論不帶 groupId（整個課程共用）
      navigate(`/student/cross-group/${courseDispatchId}`);
    }
  };

  // 獲取關鍵提問（從 ApplicationTask，過濾 use_brainstorm_wall = true）
  const fetchKeyQuestions = async () => {
    try {
      const response = await axiosInstance.get(`${config.getApplicationTasks}${courseDispatchId}`);
      if (response.data.success) {
        // 過濾出關鍵提問（use_brainstorm_wall = true）
        const keyQuestions = response.data.data.filter(task => task.use_brainstorm_wall === true);
        setTeacherKeyQuestions(keyQuestions);
      }
    } catch (error) {
      console.error('獲取關鍵提問資料錯誤:', error);
      setError('獲取關鍵提問資料失敗');
    }
  };

  // 從 JWT token 獲取當前用戶 ID
  const getCurrentUserId = () => {
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) return null;
      
      // 解碼 JWT token (base64)
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const decoded = JSON.parse(jsonPayload);
      return decoded.dataValues?.id || decoded.id || decoded.userId || null;
    } catch (error) {
      console.error('解碼 JWT token 錯誤:', error);
      return null;
    }
  };

  // 獲取問題列表
  const fetchQuestions = async () => {
    try {
      const response = await axiosInstance.get(`${config.getCourseQuestions}${courseDispatchId}`);
      if (response.data.success) {
        setQuestions(response.data.data);
        
        // 追蹤學生已提出問題的 task_id
        // 檢查問題列表：如果某個問題的 author_id 是當前學生，且該問題有 task_id，則標記該 task_id 為已提出
        const submittedIds = new Set();
        const currentUserId = getCurrentUserId();
        
        if (currentUserId) {
          response.data.data.forEach(q => {
            // 檢查是否為當前學生提出的問題
            const questionAuthorId = q.author_id || q.Author?.id;
            if (questionAuthorId && String(questionAuthorId) === String(currentUserId)) {
              // 如果該問題有關聯的 task_id，標記為已提出
              if (q.task_id) {
                submittedIds.add(q.task_id);
              }
            }
          });
        }
        
        setSubmittedTaskIds(submittedIds);
      }
    } catch (error) {
      console.error('獲取問題資料錯誤:', error);
      setError('獲取問題資料失敗');
    }
  };

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!courseDispatchId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        await Promise.all([
          fetchKeyQuestions(),
          fetchQuestions()
        ]);
      } catch (error) {
        console.error('載入資料錯誤:', error);
        setError('載入資料失敗');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [courseDispatchId]);

  // 當關鍵提問載入後，自動選擇關聯任務（若有），否則選第一個
  useEffect(() => {
    if (teacherKeyQuestions.length > 0 && !selectedTeacherQuestion) {
      // 從 sessionStorage 讀取目標任務 ID（由想法牆任務公告區點擊帶入）
      const targetTaskIdStr = sessionStorage.getItem('questionWallTargetTaskId');
      
      let initialTask = null;
      if (targetTaskIdStr) {
        const targetTaskId = parseInt(targetTaskIdStr);
        initialTask = teacherKeyQuestions.find(t => parseInt(t.id) === targetTaskId) || null;
        console.log('[QuestionWall] 從 sessionStorage 找到目標任務:', {
          targetTaskId,
          found: !!initialTask
        });
        // 用過就清掉，避免影響之後其他課程
        sessionStorage.removeItem('questionWallTargetTaskId');
      }

      // 如果沒有找到對應任務，就退回選第一個
      const finalTask = initialTask || teacherKeyQuestions[0];
      if (finalTask) {
        setSelectedTeacherQuestion(finalTask);
        setNewQuestion(prev => ({ 
          ...prev, 
          task_id: finalTask.id ? parseInt(finalTask.id) : null
        }));
      }
    }
  }, [teacherKeyQuestions, selectedTeacherQuestion]);

  // 初始化 WebSocket 連接
  useEffect(() => {
    if (courseDispatchId) {
      console.log('[問題牆] 初始化 WebSocket 連接，courseDispatchId:', courseDispatchId);
      console.log('[問題牆] WebSocket 連接 URL:', url.socketioHost);
      console.log('[問題牆] WebSocket 連接路徑:', '/s/socket.io');
      
      const socket = io.connect(url.socketioHost, { 
        path: '/s/socket.io',
        transports: ['websocket', 'polling'], // 明確指定傳輸方式
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5
      });
      setWs(socket);

      socket.on('connect', () => {
        console.log('[問題牆] WebSocket 已連接，socket.id:', socket.id);
      });

      socket.on('connect_error', (error) => {
        console.error('[問題牆] WebSocket 連接錯誤:', error);
        console.error('[問題牆] 連接失敗的 URL:', url.socketioHost);
      });

      socket.on('disconnect', (reason) => {
        console.log('[問題牆] WebSocket 已斷開，原因:', reason);
      });

      // 清理函數
      return () => {
        console.log('[問題牆] 斷開 WebSocket 連接');
        socket.disconnect();
      };
    }
  }, [courseDispatchId]);

  // WebSocket 即時更新監聽（問題牆新留言）
  useEffect(() => {
    if (ws && courseDispatchId) {
      console.log('[問題牆] 設置 WebSocket 監聽，courseDispatchId:', courseDispatchId);

      // 監聽新節點事件（問題牆類型）
      const handleNewNode = (body) => {
        console.log('🔔 [問題牆 WebSocket] ========== 收到新節點事件 ==========');
        console.log('  - 完整數據:', JSON.stringify(body, null, 2));
        console.log('  - 節點 ID:', body.id);
        console.log('  - 牆面類型:', body.wall_type);
        console.log('  - 節點類型:', body.node_type);
        console.log('  - 課程派發ID:', body.course_dispatch_id);

        // 只處理問題牆類型的節點（wall_type === 'question'）
        if (body.wall_type === 'question' && body.course_dispatch_id == courseDispatchId) {
          console.log('✅ [問題牆 WebSocket] 檢測到問題牆新問題，更新本地狀態');
          
          // 重新獲取問題列表以更新顯示
          fetchQuestions().catch(err => {
            console.error('[問題牆 WebSocket] 更新問題列表失敗:', err);
          });
        } else {
          console.log('⚠️ [問題牆 WebSocket] 忽略非問題牆節點或不同課程的節點');
        }
        console.log('========== 新節點事件處理結束 ==========');
      };

      ws.on(`node-recieve-${courseDispatchId}`, handleNewNode);

      // 清理函數
      return () => {
        console.log('[問題牆] 移除 WebSocket 監聽');
        if (ws) {
          ws.off(`node-recieve-${courseDispatchId}`, handleNewNode);
        }
      };
    }
  }, [ws, courseDispatchId]); // fetchQuestions 是函數定義，不需要作為依賴

  // 提交問題
  const handleSubmitQuestion = async () => {
    if (!newQuestion.content.trim()) {
      return;
    }

    try {
      const response = await axiosInstance.post(config.createQuestion, {
        course_dispatch_id: courseDispatchId,
        group_id: groupId ? parseInt(groupId) : null,
        task_id: newQuestion.task_id ? parseInt(newQuestion.task_id) : null,  // 關聯的關鍵提問（task_id）
        title: newQuestion.content.substring(0, 50), // 使用內容的前50字作為標題
        content: newQuestion.content
      });

      if (response.data.success) {
        // 如果有關聯的 task_id，標記為已提出
        if (newQuestion.task_id) {
          setSubmittedTaskIds(prev => new Set([...prev, parseInt(newQuestion.task_id)]));
        }
        
        // 重新獲取問題列表
        await fetchQuestions();
        
        setNewQuestion({ content: '', task_id: null });
        setOpenQuestionDialog(false);
        setSelectedTeacherQuestion(null);
      }
    } catch (error) {
      console.error('提交問題錯誤:', error);
      alert('提交問題失敗，請重試');
    }
  };

  // 選擇老師關鍵提問
  const handleSelectTeacherQuestion = (taskId) => {
    const task = teacherKeyQuestions.find(t => t.id === parseInt(taskId));
    setSelectedTeacherQuestion(task);
    setNewQuestion(prev => ({ 
      ...prev, 
      task_id: taskId ? parseInt(taskId) : null
    }));
  };


  // 檢查學生是否已針對特定關鍵提問提出過問題
  const hasSubmittedForTask = (taskId) => {
    if (!taskId) return true;  // 沒有關聯關鍵提問的問題，直接顯示
    return submittedTaskIds.has(taskId);
  };

  const getCategoryColor = (category) => {
    // 統一使用基本色，讓視覺更一致
    return 'default';
  };

  // 排序和篩選
  const filteredQuestions = questions.filter(q => {
    // 過濾掉老師派發到小組的任務（is_teacher_key_question === true）
    if (q.is_teacher_key_question === true) {
      return false;
    }
    
    // 根據分類篩選
    // if (categoryFilter !== '全部' && q.category !== categoryFilter) {
    //   return false;
    // }
    
    // 根據關鍵提問（task_id）篩選（如果選擇了關鍵提問）
    if (selectedTeacherQuestion && q.task_id !== selectedTeacherQuestion.id) {
      return false;
    }
    
    return true;
  });

  const sortedQuestions = [...filteredQuestions].sort((a, b) => {
    if (sortBy === '精選優先') {
      if (a.is_featured && !b.is_featured) return -1;
      if (!a.is_featured && b.is_featured) return 1;
    }
    if (sortBy === '最新') {
      return new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt);
    }
    if (sortBy === '最舊') {
      return new Date(a.created_at || a.createdAt) - new Date(b.created_at || b.createdAt);
    }
    if (sortBy === '按組別') {
      const groupA = a.Group?.groupName || a.author_group_name || '未分組';
      const groupB = b.Group?.groupName || b.author_group_name || '未分組';
      return groupA.localeCompare(groupB);
    }
    return 0;
  });

  // 格式化時間
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // Loading and error states
  if (loading) {
    return (
      <div className="teacher-layout class-application-question-wall-container">
        <div className="main-content-v2">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
            <Typography variant="h6" sx={{ ml: 2 }}>載入中...</Typography>
          </Box>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="teacher-layout class-application-question-wall-container">
        <div className="main-content-v2">
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="teacher-layout class-application-question-wall-container">
      <div className="main-content-v2">
        <StudentWallNavbar 
          currentWall={currentWall}
          onWallChange={handleWallChange}
          courseDispatchId={courseDispatchId}
          groupId={groupId}
        />
        
        <div className="class-header-row">
          <h1 className="main-title">
            問題牆
            <Typography variant="subtitle1" color="text.secondary" sx={{ ml: 1, display: 'inline' }}>
              - 水的三態變化
            </Typography>
          </h1>
        </div>

        <Grid container spacing={2} sx={{ height: 'calc(100vh - 200px)' }}>
          {/* 左側控制面板 */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', overflow: 'auto' }}>
              <CardContent>
                {/* 老師關鍵提問區域 */}
                <Typography variant="h6" gutterBottom sx={{ color: '#1e4d5f', fontWeight: 'bold' }}>
                  🎯 老師關鍵提問
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>選擇關鍵提問</InputLabel>
                  <Select
                    value={selectedTeacherQuestion?.id || ''}
                    onChange={(e) => handleSelectTeacherQuestion(e.target.value)}
                    label="選擇關鍵提問"
                  >
                    <MenuItem value="">
                      <em>不選擇（自由提問）</em>
                    </MenuItem>
                    {teacherKeyQuestions.map(task => (
                      <MenuItem key={task.id} value={task.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <span style={{ flex: 1 }}>{task.title}</span>
                          {!hasSubmittedForTask(task.id) && (
                            <Chip 
                              label="請先作答" 
                              size="small" 
                              color="warning"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedTeacherQuestion && (
                  <Card sx={{ mb: 2, bgcolor: '#f0f8ff', border: '2px solid #1e4d5f' }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e4d5f', mb: 1 }}>
                        {selectedTeacherQuestion.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                        {selectedTeacherQuestion.task_description || selectedTeacherQuestion.task_content || '無描述'}
                      </Typography>
                      
                      {/* 顯示「請先作答」提示 */}
                      {!hasSubmittedForTask(selectedTeacherQuestion.id) && (
                        <Alert 
                          severity="warning" 
                          icon={<WarningIcon />}
                          sx={{ mt: 1, fontSize: '0.8rem', py: 0.5 }}
                        >
                          請先針對此關鍵提問提出至少一個問題，才能查看其他同學的問題
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                  排序方式
                </Typography>
                
                {/* <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>分類篩選</InputLabel>
                  <Select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    label="分類篩選"
                  >
                    {categories.map(category => (
                      <MenuItem key={category} value={category} title={categoryDescriptions[category] || ''}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl> */}

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>排序方式</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    label="排序方式"
                  >
                    {sortOptions.map(option => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenQuestionDialog(true)}
                  sx={{ mb: 2, backgroundColor: '#1e4d5f', '&:hover': { backgroundColor: '#163a4a' } }}
                >
                  提出新問題
                </Button>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="text.secondary">
                  共 {sortedQuestions.length} 個問題
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* 右側問題列表 */}
          <Grid item xs={12} md={8}>
            <Card sx={{ height: '100%', overflow: 'auto' }}>
              <CardContent>
                {sortedQuestions.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                      目前沒有問題
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      點擊「提出新問題」開始提問
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                    {sortedQuestions.map((question, index) => (
                      <React.Fragment key={question.id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          sx={{
                            py: 2,
                            px: 2,
                            bgcolor: question.isPinned ? 'action.hover' : 'transparent',
                            '&:hover': {
                              bgcolor: 'action.hover'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flex: 1 }}>
                                  {question.is_featured && (
                                    <Chip label="精選" size="small" color="warning" sx={{ height: 18, fontSize: '0.7rem' }} />
                                  )}
                                  {question.category && question.category !== '未分類' && (
                                    <Chip 
                                      label={question.category} 
                                      size="small" 
                                      color={getCategoryColor(question.category)}
                                      sx={{ height: 18, fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Box>
                              </Box>
                              
                              <Typography variant="body1" color="text.primary" sx={{ mb: 1.5, fontSize: '1.1rem', lineHeight: 1.6, fontWeight: 400 }}>
                                {question.content}
                              </Typography>
                              
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                    {question.Author?.name || question.author_name || '未知學生'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                    {formatTime(question.created_at || question.createdAt)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 新增問題對話框 - 使用 CreateQuestion 組件（包含鷹架） */}
        <CreateQuestion
          open={openQuestionDialog}
          onClose={() => {
            setOpenQuestionDialog(false);
            setNewQuestion({ content: '', task_id: null });
            setSelectedTeacherQuestion(null);
          }}
          ws={ws}
          taskId={selectedTeacherQuestion?.id || newQuestion.task_id || null}
          courseDispatchId={courseDispatchId}
          groupId={groupId}
          wallType="question"  // 明確指定為問題牆
          onNodeCreated={async (createdNode) => {
            // 節點創建成功後，如果有關聯的 task_id，標記為已提出
            if (createdNode?.task_id) {
              setSubmittedTaskIds(prev => new Set([...prev, parseInt(createdNode.task_id)]));
            }
            // 重新獲取問題列表
            await fetchQuestions();
            // 保持選擇狀態，不清空 selectedTeacherQuestion
            setNewQuestion({ content: '', task_id: selectedTeacherQuestion?.id || null });
            // 不清空 selectedTeacherQuestion，維持在回覆的關鍵提問留言中
          }}
        />
      </div>
    </div>
  );
}
